import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent7c as accent, slide7cAccentFrom, useSlide7c, Seg } from "./slide7c.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 7c — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide7c.data.ts.
   The eight limbs stack: number + name on one line, description below. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 14.5, lineHeight: 1.6, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide7cMobile: React.FC = () => {
  const { meta: slide7cMeta, limbs: slide7cLimbs, intro: slide7cIntro, callout: slide7cCallout } = useSlide7c();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#4a9494", textTransform: "uppercase", marginBottom: 8 }}>
        {slide7cMeta.breadcrumbPre} · <span style={{ color: accent }}>{slide7cMeta.breadcrumbAccent}</span>
      </div>

      {/* sanskrit overline */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 3, color: "rgba(56,181,181,0.55)", marginBottom: 6 }}>
        {slide7cMeta.sanskritOverline}
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 25, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide7cMeta.titlePre}<span style={{ color: accent }}>{slide7cMeta.titleAccentWord}</span>{slide7cMeta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#9bbdd6", margin: "0 0 14px", lineHeight: 1.5 }}>
        {slide7cMeta.subtitle}
      </p>

      {/* intro */}
      <p style={{ ...txt, color: "#aacdd8", marginBottom: 20 }}>{renderSegs(slide7cIntro, "#bfeaea")}</p>

      {/* eight limbs */}
      <div style={{ borderTop: "1px solid rgba(56,181,181,0.12)", paddingTop: 14, marginBottom: 18 }}>
        {slide7cLimbs.map((l, i) => (
          <div key={i} style={{ marginBottom: i === slide7cLimbs.length - 1 ? 0 : 13 }}>
            <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 14.5, color: i >= slide7cAccentFrom ? accent : "#a0dcdc", marginBottom: 3 }}>
              {l.n} <span style={{ color: "#bfeaea" }}>· {l.san}</span>
            </div>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13, lineHeight: 1.5, color: "#9bbdd6", margin: 0 }}>{l.desc}</p>
          </div>
        ))}
      </div>

      {/* callout */}
      <div style={{ background: "rgba(56,181,181,0.06)", border: "1px solid rgba(56,181,181,0.22)", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
        <div style={lbl}>{slide7cMeta.calloutLabel}</div>
        <p style={{ ...txt, color: "#9adcdc" }}>{renderSegs(slide7cCallout, "#c8f0f0")}</p>
      </div>

      {/* attribution */}
      <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: 1.5, color: "rgba(120,200,200,0.5)", textAlign: "right" }}>
        {slide7cMeta.attribution}
      </div>
    </div>
  );
};

export default HTLDSlide7cMobile;
